import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {getApp} from 'firebase/app';
import {getFunctions, httpsCallable} from 'firebase/functions';

const BackdropCreator = ({
    onAddToProject,
    onClose
}) => {
    const [prompt, setPrompt] = useState('');
    const [imageBase64, setImageBase64] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [creditUsage, setCreditUsage] = useState(null);

    const handleGenerate = async () => {
        const cleanPrompt = prompt.trim();

        if (!cleanPrompt) {
            setError('Describe the backdrop you want to create.');
            return;
        }

        setLoading(true);
        setError('');
        setImageBase64('');
        setCreditUsage(null);

        try {
            const functions = getFunctions(
                getApp(),
                'us-central1'
            );

            const generateScratchBackdrop = httpsCallable(
                functions,
                'generateScratchBackdrop'
            );

            const requestId = `${Date.now()}_${
                Math.random().toString(36).slice(2)
            }`;

            const result = await generateScratchBackdrop({
                prompt: cleanPrompt,
                requestId
            });

            const generatedImage =
                result.data && result.data.imageBase64;

            if (!generatedImage) {
                throw new Error('No image returned.');
            }

            setImageBase64(generatedImage);

            if (result.data && result.data.creditUsage) {
                setCreditUsage(result.data.creditUsage);
            }
        } catch (err) {
            console.error(
                'AI backdrop generation failed:',
                err
            );

            if (
                err &&
                (
                    err.code === 'functions/failed-precondition' ||
                    err.code === 'failed-precondition'
                )
            ) {
                setError(
                    'You do not have enough AI Credits for this generation.'
                );
            } else {
                setError(
                    'Unable to create the backdrop. Please try again.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const handleAddToProject = () => {
        if (!imageBase64) return;

        onAddToProject(imageBase64);
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.iconBubble}>
                    🌄
                </div>

                <div style={styles.headerText}>
                    <div style={styles.eyebrow}>
                        AI CREATIVE TOOL
                    </div>

                    <div style={styles.heading}>
                        Create a Backdrop with AI
                    </div>

                    {!imageBase64 && (
                        <div style={styles.subtitle}>
                            Describe a scene and add it as a new
                            backdrop to your project.
                        </div>
                    )}
                </div>
            </div>

            {/* Creation form */}
            {!imageBase64 && (
                <>
                    {/* Quick example */}
                    <div style={styles.exampleCard}>
                        <span style={styles.exampleLabel}>
                            💡 Try:
                        </span>

                        {' '}

                        A colorful classroom, playground, jungle,
                        home, store, or outdoor scene.
                    </div>

                    {/* Credits */}
                    <div style={styles.creditCard}>
                        <div style={styles.creditHeader}>
                            <div style={styles.creditTitle}>
                                ✨ AI Backdrop
                            </div>

                            <div style={styles.costBadge}>
                                1 CREDIT
                            </div>
                        </div>

                        <div style={styles.freeOffer}>
                            <div style={styles.freeOfferBadge}>
                                1 FREE
                            </div>

                            <div style={styles.freeOfferText}>
                                Your first AI Backdrop generation
                                is free.
                            </div>
                        </div>

                        <div style={styles.creditFooter}>
                            After your free generation · 1 AI Credit each
                        </div>
                    </div>

                    {/* Steps */}
                    <div style={styles.steps}>
                        <div style={styles.step}>
                            <span style={styles.stepNumber}>
                                1
                            </span>
                            Describe
                        </div>

                        <span style={styles.stepArrow}>
                            →
                        </span>

                        <div style={styles.step}>
                            <span style={styles.stepNumber}>
                                2
                            </span>
                            Generate
                        </div>

                        <span style={styles.stepArrow}>
                            →
                        </span>

                        <div style={styles.step}>
                            <span style={styles.stepNumber}>
                                3
                            </span>
                            Add Backdrop
                        </div>
                    </div>

                    {/* Prompt */}
                    <label style={styles.label}>
                        Describe your backdrop
                    </label>

                    <textarea
                        style={styles.prompt}
                        value={prompt}
                        placeholder="Example: A colorful jungle with a waterfall at sunset..."
                        maxLength={1000}
                        disabled={loading}
                        onChange={event => setPrompt(event.target.value)}
                        onKeyDown={event => event.stopPropagation()}
                        onKeyUp={event => event.stopPropagation()}
                    />

                    <div style={styles.promptFooter}>
                        <span>
                            Add details like location, colors,
                            lighting, objects, or atmosphere.
                        </span>

                        <span>
                            {prompt.length}/1000
                        </span>
                    </div>

                    {/* Information */}
                    <div style={styles.infoCard}>
                        <span style={styles.infoIcon}>
                            ＋
                        </span>

                        <span>
                            The generated image will be added as a
                            new backdrop. Your existing backdrops
                            will remain available.
                        </span>
                    </div>

                    {/* Error */}
                    {error && (
                        <div style={styles.error}>
                            <span style={styles.errorIcon}>
                                !
                            </span>

                            <span>
                                {error}
                            </span>
                        </div>
                    )}

                    {/* Generate */}
                    <button
                        type="button"
                        style={{
                            ...styles.generateButton,
                            ...(
                                loading || !prompt.trim() ?
                                    styles.disabledButton :
                                    {}
                            )
                        }}
                        disabled={loading || !prompt.trim()}
                        onClick={handleGenerate}
                    >
                        {loading ? (
                            <>
                                <span>✨</span>
                                Creating Backdrop...
                            </>
                        ) : (
                            <>
                                <span>✨</span>
                                Generate Backdrop
                            </>
                        )}
                    </button>
                </>
            )}

            {/* Result */}
            {imageBase64 && (
                <div style={styles.result}>
                    {/* Credit result */}
                    {creditUsage && (
                        <div style={styles.usageResult}>
                            <span style={styles.successIcon}>
                                ✓
                            </span>

                            <div style={styles.usageContent}>
                                <div style={styles.usageTitle}>
                                    {creditUsage.freeUse ?
                                        'Free Backdrop generation used' :
                                        `${creditUsage.creditsUsed} AI ${
                                            creditUsage.creditsUsed === 1 ?
                                                'Credit' :
                                                'Credits'
                                        } used`
                                    }
                                </div>

                                <div style={styles.usageDetails}>
                                    {
                                        creditUsage
                                            .freeScratchBackdropsRemaining
                                    }
                                    {' free remaining · '}
                                    {creditUsage.aiCredits}
                                    {' AI '}
                                    {creditUsage.aiCredits === 1 ?
                                        'Credit' :
                                        'Credits'
                                    }
                                    {' remaining'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview */}
                    <div style={styles.previewCard}>
                        <img
                            style={styles.preview}
                            src={`data:image/png;base64,${imageBase64}`}
                            alt="AI generated backdrop"
                        />
                    </div>

                    {/* Ready */}
                    <div style={styles.readyMessage}>
                        <strong>
                            🎉 Your backdrop is ready!
                        </strong>

                        <span style={styles.readyText}>
                            {' '}
                            Add it as a new backdrop without
                            replacing your existing backdrops.
                        </span>
                    </div>

                    {/* Actions */}
                    <div style={styles.actions}>
                        <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={handleAddToProject}
                        >
                            ＋ Add Backdrop
                        </button>

                        <button
                            type="button"
                            style={{
                                ...styles.secondaryButton,
                                ...(loading ?
                                    styles.disabledButton :
                                    {})
                            }}
                            disabled={loading}
                            onClick={handleGenerate}
                        >
                            {loading ?
                                '✨ Creating...' :
                                '✨ Generate Again'
                            }
                        </button>
                    </div>

                    {error && (
                        <div style={styles.resultError}>
                            <span style={styles.errorIcon}>
                                !
                            </span>

                            <span>
                                {error}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Cancel */}
            {onClose && (
                <button
                    type="button"
                    style={styles.cancel}
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </button>
            )}
        </div>
    );
};

BackdropCreator.propTypes = {
    onAddToProject: PropTypes.func.isRequired,
    onClose: PropTypes.func
};

BackdropCreator.defaultProps = {
    onClose: null
};

const styles = {
    container: {
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '540px',
        padding: '18px',
        color: '#25324a',
        fontFamily: 'inherit'
    },

    header: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '11px',
        marginBottom: '11px'
    },

    headerText: {
        flex: 1,
        minWidth: 0
    },

    iconBubble: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        borderRadius: '12px',
        background: '#eee9ff',
        fontSize: '19px'
    },

    eyebrow: {
        marginBottom: '2px',
        color: '#7559d9',
        fontSize: '9px',
        fontWeight: '800',
        letterSpacing: '1px'
    },

    heading: {
        marginBottom: '3px',
        color: '#18233a',
        fontSize: '19px',
        fontWeight: '800',
        lineHeight: '1.2'
    },

    subtitle: {
        color: '#667085',
        fontSize: '12px',
        lineHeight: '1.35'
    },

    exampleCard: {
        marginBottom: '9px',
        padding: '8px 10px',
        border: '1px solid #e6e9ef',
        borderRadius: '9px',
        background: '#fafbfd',
        color: '#687386',
        fontSize: '10px',
        lineHeight: '1.4'
    },

    exampleLabel: {
        color: '#654dbb',
        fontWeight: '800'
    },

    creditCard: {
        marginBottom: '10px',
        padding: '9px 11px',
        border: '1px solid #ddd3ff',
        borderRadius: '10px',
        background: '#f8f6ff'
    },

    creditHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '10px',
        marginBottom: '7px'
    },

    creditTitle: {
        color: '#5f46bd',
        fontSize: '11px',
        fontWeight: '800'
    },

    costBadge: {
        flexShrink: 0,
        padding: '3px 7px',
        borderRadius: '12px',
        background: '#7559d9',
        color: '#ffffff',
        fontSize: '8px',
        fontWeight: '800',
        letterSpacing: '.4px'
    },

    freeOffer: {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        padding: '6px 8px',
        borderRadius: '8px',
        background: '#ffffff'
    },

    freeOfferBadge: {
        flexShrink: 0,
        padding: '3px 7px',
        borderRadius: '12px',
        background: '#e8e0ff',
        color: '#5f46bd',
        fontSize: '9px',
        fontWeight: '900'
    },

    freeOfferText: {
        color: '#615b6c',
        fontSize: '9px',
        fontWeight: '600',
        lineHeight: '1.35'
    },

    creditFooter: {
        marginTop: '6px',
        color: '#777083',
        fontSize: '8px',
        fontWeight: '600',
        textAlign: 'center'
    },

    steps: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        marginBottom: '10px'
    },

    step: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        color: '#5d6879',
        fontSize: '9px',
        fontWeight: '700'
    },

    stepNumber: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '17px',
        height: '17px',
        borderRadius: '50%',
        background: '#edf0f7',
        color: '#7559d9',
        fontSize: '8px',
        fontWeight: '800'
    },

    stepArrow: {
        color: '#b5bbc5',
        fontSize: '10px'
    },

    label: {
        display: 'block',
        marginBottom: '5px',
        color: '#344054',
        fontSize: '12px',
        fontWeight: '750'
    },

    prompt: {
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '82px',
        padding: '9px 10px',
        border: '1px solid #cfd6e1',
        borderRadius: '9px',
        outline: 'none',
        background: '#ffffff',
        color: '#26344d',
        fontFamily: 'inherit',
        fontSize: '12px',
        lineHeight: '1.4',
        resize: 'vertical'
    },

    promptFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '15px',
        marginTop: '4px',
        marginBottom: '8px',
        color: '#9299a6',
        fontSize: '8px',
        lineHeight: '1.3'
    },

    infoCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        marginBottom: '9px',
        padding: '7px 9px',
        borderRadius: '8px',
        background: '#f4f7fb',
        color: '#667085',
        fontSize: '9px',
        lineHeight: '1.35'
    },

    infoIcon: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        width: '17px',
        height: '17px',
        borderRadius: '50%',
        background: '#e7ebf2',
        color: '#5f6c80',
        fontSize: '10px',
        fontWeight: '800'
    },

    error: {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        marginBottom: '9px',
        padding: '8px 9px',
        border: '1px solid #f2c6ca',
        borderRadius: '8px',
        background: '#fff6f7',
        color: '#a73b45',
        fontSize: '10px',
        lineHeight: '1.35'
    },

    resultError: {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        marginTop: '9px',
        padding: '8px 9px',
        border: '1px solid #f2c6ca',
        borderRadius: '8px',
        background: '#fff6f7',
        color: '#a73b45',
        fontSize: '10px',
        lineHeight: '1.35'
    },

    errorIcon: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        width: '17px',
        height: '17px',
        borderRadius: '50%',
        background: '#a73b45',
        color: '#ffffff',
        fontSize: '9px',
        fontWeight: '800'
    },

    generateButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '7px',
        width: '100%',
        padding: '10px 14px',
        border: 'none',
        borderRadius: '9px',
        background: '#7559d9',
        color: '#ffffff',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '12px',
        fontWeight: '800',
        boxShadow: '0 4px 12px rgba(117, 89, 217, 0.20)'
    },

    disabledButton: {
        opacity: 0.55,
        cursor: 'default',
        boxShadow: 'none'
    },

    result: {
        marginTop: '2px'
    },

    usageResult: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '9px',
        padding: '7px 9px',
        border: '1px solid #cbe6d2',
        borderRadius: '8px',
        background: '#f4fbf6'
    },

    usageContent: {
        minWidth: 0
    },

    successIcon: {
        display: 'flex',
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        background: '#d8f2df',
        color: '#26763b',
        fontSize: '11px',
        fontWeight: '900'
    },

    usageTitle: {
        color: '#28623a',
        fontSize: '10px',
        fontWeight: '800'
    },

    usageDetails: {
        marginTop: '1px',
        color: '#66806c',
        fontSize: '9px'
    },

    previewCard: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '210px',
        padding: '8px',
        border: '1px solid #e3e7ee',
        borderRadius: '10px',
        background: '#f8fafc',
        boxSizing: 'border-box',
        overflow: 'hidden'
    },

    preview: {
        display: 'block',
        maxWidth: '100%',
        maxHeight: '194px',
        objectFit: 'contain'
    },

    readyMessage: {
        padding: '8px 2px',
        color: '#344054',
        fontSize: '11px',
        lineHeight: '1.35'
    },

    readyText: {
        color: '#7a8493',
        fontSize: '10px'
    },

    actions: {
        display: 'flex',
        gap: '8px'
    },

    primaryButton: {
        flex: 1,
        padding: '9px 12px',
        border: 'none',
        borderRadius: '9px',
        background: '#7559d9',
        color: '#ffffff',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '11px',
        fontWeight: '800'
    },

    secondaryButton: {
        flex: 1,
        padding: '9px 12px',
        border: '1px solid #cfc5f5',
        borderRadius: '9px',
        background: '#ffffff',
        color: '#654dbb',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '11px',
        fontWeight: '750'
    },

    cancel: {
        display: 'block',
        margin: '9px auto 0',
        padding: '4px 9px',
        border: 'none',
        background: 'transparent',
        color: '#8a93a2',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: '10px'
    }
};

export default BackdropCreator;